import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, shortHash, shortAddr, timeAgo, formatEth, formatNumber } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, ShieldCheck, RefreshCw, Coins, ScrollText, Send, Flame, AlertCircle } from "lucide-react";

const STATUSES = ["All", "Success", "Pending", "Failed"];
const ASSETS = ["ETH", "USDT", "USDC", "LINK", "DAI"];

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const nav = useNavigate();

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 25;

  // filters
  const [status, setStatus] = useState("All");
  const [address, setAddress] = useState("");
  const [hash, setHash] = useState("");
  const [date, setDate] = useState("");

  // mint form
  const [mintAddr, setMintAddr] = useState("");
  const [mintAsset, setMintAsset] = useState("ETH");
  const [mintAmount, setMintAmount] = useState("");
  const [minting, setMinting] = useState(false);

  // create-tx form asset selector
  const [tokens, setTokens] = useState([]);
  const [ctxAsset, setCtxAsset] = useState("ETH");

  // tooltip messages (for /address and /txs status icons)
  const [tooltips, setTooltips] = useState({ failed: "", pending: "" });
  const loadTooltips = useCallback(async () => {
    try {
      const r = await api.get("/settings/tooltips");
      setTooltips(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get("/tokens").then((r) => setTokens(r.data.items || r.data || [])).catch(() => {});
    loadTooltips();
  }, [loadTooltips]);

  useEffect(() => {
    if (checked && !user) nav("/admin/login");
  }, [checked, user, nav]);

  const load = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/transactions", {
          params: {
            status: status === "All" ? undefined : status,
            address: address || undefined,
            hash: hash || undefined,
            date: date || undefined,
            limit: perPage,
            skip: (page - 1) * perPage,
          },
        }),
      ]);
      setStats(s.data);
      setItems(list.data.items);
      setTotal(list.data.total);
    } catch {
      /* ignore */
    }
  }, [status, address, hash, date, page]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!checked || !user) return <Layout><p>Loading…</p></Layout>;

  const changeStatus = async (h, newStatus) => {
    try {
      await api.patch(`/admin/transactions/${h}/status`, { status: newStatus });
      toast.success(`Transaction ${shortHash(h, 6, 4)} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const mint = async (e) => {
    e.preventDefault();
    if (!mintAddr || !mintAmount) return;
    setMinting(true);
    try {
      await api.post("/admin/wallets/mint", {
        address: mintAddr.trim(),
        asset: mintAsset,
        amount: Number(mintAmount),
      });
      toast.success(`Minted ${mintAmount} ${mintAsset} to ${shortAddr(mintAddr)}`);
      setMintAmount("");
    } catch (ex) {
      const d = ex.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Mint failed");
    } finally { setMinting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <Layout>
      {/* Admin header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="ks-icon-square"><ShieldCheck size={18} /></div>
          <div>
            <h1 className="text-xl font-semibold" data-testid="admin-title">Admin Panel</h1>
            <p className="text-xs text-[var(--ks-text-soft)]">Signed in as {user.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="admin-nav-logs"><Link to="/admin/logs"><ScrollText size={14} className="mr-1"/>Logs</Link></Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-refresh"><RefreshCw size={14} className="mr-1"/>Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout"><LogOut size={14} className="mr-1"/>Logout</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <StatMini label="Total Txs" value={formatNumber(stats?.total_transactions)} testId="astat-total" />
        <StatMini label="Success" value={formatNumber(stats?.success)} tone="success" testId="astat-success" />
        <StatMini label="Pending" value={formatNumber(stats?.pending)} tone="pending" testId="astat-pending" />
        <StatMini label="Failed" value={formatNumber(stats?.failed)} tone="failed" testId="astat-failed" />
        <StatMini label="Wallets" value={formatNumber(stats?.wallets)} testId="astat-wallets" />
        <StatMini label="Blocks" value={formatNumber(stats?.blocks)} testId="astat-blocks" />
      </div>

      {/* Mint tool */}
      <div className="ks-card mb-6">
        <div className="ks-card-header">
          <span className="flex items-center gap-2"><Coins size={16}/> Mint ETH / Tokens to any Wallet</span>
        </div>
        <form onSubmit={mint} className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-6">
            <Label>Wallet Address</Label>
            <Input value={mintAddr} onChange={(e) => setMintAddr(e.target.value)} placeholder="0x…" required data-testid="mint-address"/>
          </div>
          <div className="md:col-span-2">
            <Label>Asset</Label>
            <Select value={mintAsset} onValueChange={setMintAsset}>
              <SelectTrigger data-testid="mint-asset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSETS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Amount</Label>
            <Input type="number" step="any" min="0" value={mintAmount} onChange={(e) => setMintAmount(e.target.value)} placeholder="30" required data-testid="mint-amount"/>
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button type="submit" disabled={minting} className="w-full bg-[var(--ks-primary)] hover:bg-[var(--ks-link-hover)]" data-testid="mint-submit">
              {minting ? "Minting…" : "Mint"}
            </Button>
          </div>
        </form>
        <p className="text-xs text-[var(--ks-text-soft)] px-4 pb-3">
          Example: mint <span className="font-mono">30 ETH</span> or <span className="font-mono">1000 USDT</span> to any wallet address. If the wallet does not exist, it will be created.
        </p>
      </div>

      {/* Network Settings */}
      <div className="ks-card mb-6" data-testid="settings-card">
        <div className="ks-card-header"><span className="flex items-center gap-2"><Flame size={16}/> Network Settings — Gas Price Override</span></div>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            try {
              await api.post("/admin/settings/gas", { gas_price_gwei: Number(fd.get("gas")) });
              toast.success(`Gas price set to ${fd.get("gas")} Gwei — visible everywhere`);
              load();
            } catch { toast.error("Failed to set gas price"); }
          }}
          className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3"
        >
          <div className="md:col-span-8"><Label>Custom Gas Price (Gwei) — shown across entire platform (top ticker, homepage, stats)</Label><Input name="gas" type="number" step="any" min="0" placeholder="e.g. 22.5" required data-testid="gas-input"/></div>
          <div className="md:col-span-2 flex items-end">
            <Button type="submit" className="w-full bg-[var(--ks-primary)] hover:bg-[var(--ks-link-hover)]" data-testid="gas-submit">Set Gas</Button>
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button type="button" variant="outline" className="w-full" data-testid="gas-clear"
              onClick={async () => {
                await api.post("/admin/settings/gas", { gas_price_gwei: -1 }).catch(() => {});
                toast.success("Reset — using live network gas");
                load();
              }}>Reset</Button>
          </div>
        </form>
      </div>

      {/* Status Tooltips */}
      <div className="ks-card mb-6" data-testid="tooltips-card">
        <div className="ks-card-header"><span className="flex items-center gap-2"><AlertCircle size={16}/> Status Tooltips — Custom messages for Failed / Pending icons</span></div>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            try {
              await api.post("/admin/settings/tooltips", {
                failed: fd.get("failed"),
                pending: fd.get("pending"),
              });
              toast.success("Tooltip messages updated — visible everywhere");
              loadTooltips();
            } catch (ex) {
              const d = ex.response?.data?.detail;
              toast.error(typeof d === "string" ? d : "Failed to update tooltips");
            }
          }}
          className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3"
        >
          <div className="md:col-span-6">
            <Label>🔴 Failed / Error tooltip (red icon)</Label>
            <Input
              name="failed"
              defaultValue={tooltips.failed || ""}
              placeholder="Warning! Error encountered during contract execution [Out of gas]"
              maxLength={500}
              data-testid="tooltip-failed-input"
              key={`failed-${tooltips.failed}`}
            />
          </div>
          <div className="md:col-span-4">
            <Label>🟡 Pending tooltip (amber icon)</Label>
            <Input
              name="pending"
              defaultValue={tooltips.pending || ""}
              placeholder="Pending"
              maxLength={500}
              data-testid="tooltip-pending-input"
              key={`pending-${tooltips.pending}`}
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <Button type="submit" className="w-full bg-[var(--ks-primary)] hover:bg-[var(--ks-link-hover)]" data-testid="tooltip-save">Save</Button>
          </div>
          <div className="md:col-span-1 flex items-end">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              data-testid="tooltip-reset"
              onClick={async () => {
                try {
                  await api.post("/admin/settings/tooltips/reset");
                  toast.success("Tooltips reset to defaults");
                  loadTooltips();
                } catch { toast.error("Failed to reset tooltips"); }
              }}
            >Reset</Button>
          </div>
        </form>
      </div>

      {/* Create Transaction */}
      <div className="ks-card mb-6" data-testid="create-tx-card">
        <div className="ks-card-header"><span className="flex items-center gap-2"><Send size={16}/> Create Transaction</span></div>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.target);
            try {
              await api.post("/admin/transactions/create", {
                from_address: fd.get("from"), to_address: fd.get("to"),
                value_eth: Number(fd.get("value")), status: fd.get("status"),
                asset: ctxAsset,
              });
              const assetLabel = ctxAsset === "ETH"
                ? "ETH"
                : (tokens.find((t) => t.contract === ctxAsset)?.symbol || "TOKEN");
              toast.success(`Transaction created (${fd.get("status")}) · ${fd.get("value")} ${assetLabel}`);
              e.target.reset();
              setCtxAsset("ETH");
              load();
            } catch (ex) {
              const d = ex.response?.data?.detail;
              toast.error(typeof d === "string" ? d : "Failed to create transaction");
            }
          }}
          className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3"
        >
          <div className="md:col-span-3"><Label>From Address</Label><Input name="from" placeholder="0x…" required data-testid="ctx-from"/></div>
          <div className="md:col-span-3"><Label>To Address</Label><Input name="to" placeholder="0x…" required data-testid="ctx-to"/></div>
          <div className="md:col-span-2"><Label>Value</Label><Input name="value" type="number" step="any" min="0" defaultValue="1" required data-testid="ctx-value"/></div>
          <div className="md:col-span-2"><Label>Asset</Label>
            <Select value={ctxAsset} onValueChange={setCtxAsset}>
              <SelectTrigger data-testid="ctx-asset"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="ETH" data-testid="ctx-asset-ETH">ETH (Ether)</SelectItem>
                {tokens.map((t) => (
                  <SelectItem
                    key={t.contract}
                    value={t.contract}
                    data-testid={`ctx-asset-${t.symbol}`}
                  >
                    {t.symbol} · {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1"><Label>Status</Label>
            <Select name="status" defaultValue="Pending">
              <SelectTrigger data-testid="ctx-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Success">Success</SelectItem>
                <SelectItem value="Failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1 flex items-end">
            <Button type="submit" className="w-full bg-[var(--ks-primary)] hover:bg-[var(--ks-link-hover)]" data-testid="ctx-submit">Send</Button>
          </div>
        </form>
      </div>

      {/* Filters */}
      <div className="ks-card mb-4">
        <div className="ks-card-header"><span>Transactions</span></div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Label>Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="0x…" data-testid="filter-address"/>
          </div>
          <div className="md:col-span-3">
            <Label>Tx Hash</Label>
            <Input value={hash} onChange={(e) => setHash(e.target.value)} placeholder="0x…" data-testid="filter-hash"/>
          </div>
          <div className="md:col-span-2">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="filter-date"/>
          </div>
          <div className="md:col-span-1 flex items-end">
            <Button type="button" onClick={() => { setPage(1); load(); }} className="w-full bg-[var(--ks-primary)] hover:bg-[var(--ks-link-hover)]" data-testid="filter-apply">Apply</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="ks-table" data-testid="admin-txs-table">
            <thead>
              <tr>
                <th>Txn Hash</th><th>Block</th><th>Age</th><th>From</th><th>To</th><th>Value</th><th>Status</th><th className="text-right">Change Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.hash} data-testid={`admin-tx-row-${t.hash}`}>
                  <td><Link to={`/tx/${t.hash}`} className="ks-link mono">{shortHash(t.hash)}</Link></td>
                  <td><Link to={`/block/${t.block_number}`} className="ks-link">{formatNumber(t.block_number)}</Link></td>
                  <td>{timeAgo(t.timestamp)}</td>
                  <td><Link to={`/address/${t.from_address}`} className="ks-link mono">{shortAddr(t.from_address)}</Link></td>
                  <td><Link to={`/address/${t.to_address}`} className="ks-link mono">{shortAddr(t.to_address)}</Link></td>
                  <td>{formatEth(t.value_eth, 5)} ETH</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="text-right">
                    <Select value={t.status} onValueChange={(v) => changeStatus(t.hash, v)}>
                      <SelectTrigger className="w-[130px] ml-auto" data-testid={`admin-tx-status-${t.hash}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Success">Completed</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-[var(--ks-text-soft)]">No transactions match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--ks-border)] text-sm">
          <span className="text-[var(--ks-text-soft)]">{formatNumber(total)} results · Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="admin-next">Next</Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const StatMini = ({ label, value, tone, testId }) => {
  const toneClass = {
    success: "text-[var(--ks-success)]",
    pending: "text-[var(--ks-pending)]",
    failed: "text-[var(--ks-failed)]",
  }[tone] || "text-[var(--ks-text)]";
  return (
    <div className="ks-card p-3" data-testid={testId}>
      <div className="ks-stat-label">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value ?? "—"}</div>
    </div>
  );
};
